/*
 * PartnerDetailPage — /partners/:slug
 * ====================================
 * Iter 69 · redesigned to match AcademyLandingPage design tokens.
 * "Register for the Upcoming Season" CTA now points to /academy/registration
 * (not /join), with ?partner={slug} for tracking.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import * as Icons from "lucide-react";
import {
  Globe, Facebook, Instagram, Youtube, Linkedin, ExternalLink,
  ChevronLeft, Award, Handshake, User,
} from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_ICONS = {
  instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
  youtube: Youtube, tiktok: Globe, website: Globe,
};

const getIcon = (name) => Icons[name] || Award;

// Iter 69 · Partner CTA URLs pointing to /join or /academy/register* are
// rewritten to /academy/registration with partner tracking preserved.
const rewriteCta = (url, slug) => {
  if (!url) return "#";
  if (url.startsWith("/join") || url.startsWith("/academy/register")) {
    return `/academy/registration?partner=${slug}`;
  }
  return url;
};

const PartnerDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/partners/${slug}`)
      .then(r => setP(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500">Φορτωση…</div>;
  if (notFound || !p) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 text-center">
        <Handshake size={40} className="text-zinc-700 mb-3" />
        <h2 className="font-['Bebas_Neue'] text-2xl text-white">Δεν βρεθηκε</h2>
        <p className="text-zinc-500 text-sm mt-1">Ο συνεργατης δεν υπαρχει η δεν ειναι ενεργος.</p>
        <button onClick={() => navigate("/partners")} className="mt-4 text-[#F5A623] hover:underline text-sm">← Πισω στους Συνεργατες</button>
      </div>
    );
  }

  const typeLabel = p.partner_type === "technical" ? "Τεχνικος Συνεργατης" : "Συνεργατης";

  return (
    <div className="min-h-screen bg-black" data-testid="partner-detail-page">
      <SEO title={`${p.name} · Συνεργατης · Λευτεριά FC`} description={p.tagline || ""} image={p.hero_image_url} />

      {/* Back link */}
      <div className="max-w-7xl mx-auto px-6 pt-32">
        <Link to="/partners" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#F5A623] transition-colors">
          <ChevronLeft size={14} /> Ολοι οι Συνεργατες
        </Link>
      </div>

      {/* 1. HERO */}
      <section className="pt-6 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <span className="badge badge-secondary mb-4">Επισημος {typeLabel}</span>
          <h1 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white tracking-wide">
            {p.name.split(" ").length > 1 ? (
              <>
                {p.name.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-[#F5A623]">{p.name.split(" ").slice(-1)}</span>
              </>
            ) : (
              <span className="text-[#F5A623]">{p.name}</span>
            )}
          </h1>
          {p.tagline && (
            <p className="text-zinc-400 text-lg max-w-2xl mt-6 leading-relaxed">{p.tagline}</p>
          )}
        </div>
      </section>

      {p.hero_image_url && (
        <div className="max-w-7xl mx-auto px-6 mb-16">
          <div className="h-64 md:h-96 overflow-hidden rounded" style={{ backgroundImage: `url(${p.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        </div>
      )}

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6"><div className="h-px bg-[#262626]" /></div>

      {/* 2. EXECUTIVE PROFILE */}
      {(p.executive_name || p.bio || (p.bio_bullets || []).length > 0) && (
        <section className="py-16 px-6" data-testid="executive-profile">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">
              EXECUTIVE <span className="text-[#F5A623]">PROFILE</span>
            </h2>
            <div className="w-12 h-1 bg-[#F5A623] mb-10" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-1">
                {p.headshot_url ? (
                  <img src={p.headshot_url} alt={p.executive_name || p.name} loading="lazy" className="w-full aspect-square object-cover rounded border border-[#262626]" />
                ) : (
                  <div className="w-full aspect-square rounded bg-gradient-to-br from-[#1a1a1a] to-black border border-[#262626] flex items-center justify-center">
                    <User size={48} className="text-zinc-700" />
                  </div>
                )}
                {p.executive_name && (
                  <div className="mt-4">
                    <p className="font-['Bebas_Neue'] text-2xl text-white leading-none tracking-wide">{p.executive_name}</p>
                    {p.executive_role && <p className="text-sm text-[#F5A623] mt-2">{p.executive_role}</p>}
                  </div>
                )}
              </div>
              <div className="lg:col-span-2 space-y-6">
                {p.bio && <p className="text-zinc-300 leading-relaxed whitespace-pre-line text-base">{p.bio}</p>}
                {(p.bio_bullets || []).length > 0 && (
                  <ul className="space-y-3">
                    {p.bio_bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623] mt-2.5 flex-shrink-0" />
                        <span className="text-zinc-200 text-base">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 3. TECHNICAL STAFF */}
      {(p.staff_members || []).length > 0 && (
        <section className="py-16 px-6 border-t border-[#262626]" data-testid="staff-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">
              ΤΕΧΝΙΚΟ <span className="text-[#F5A623]">ΕΠΙΤΕΛΕΙΟ</span>
            </h2>
            <div className="w-12 h-1 bg-[#F5A623] mb-10" />

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {p.staff_members.map((s, i) => (
                <div key={i} className="bg-[#111] border border-[#262626] rounded overflow-hidden hover:border-[#F5A623] transition-colors">
                  {s.photo_url ? (
                    <img src={s.photo_url} alt={s.name} loading="lazy" className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-gradient-to-br from-[#1a1a1a] to-black flex items-center justify-center">
                      <User size={28} className="text-zinc-700" />
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-white text-sm font-semibold truncate">{s.name}</p>
                    {s.role && <p className="text-[11px] text-[#F5A623] truncate mt-0.5">{s.role}</p>}
                    {s.bio && <p className="text-[11px] text-zinc-500 mt-2 line-clamp-2">{s.bio}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 4. PARTNERSHIP OBJECTIVES */}
      {(p.benefits || []).length > 0 && (
        <section className="py-16 px-6 border-t border-[#262626]" data-testid="benefits-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">
              ΣΤΟΧΟΙ & <span className="text-[#F5A623]">ΟΦΕΛΗ</span>
            </h2>
            <div className="w-12 h-1 bg-[#F5A623] mb-10" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {p.benefits.map((b, i) => {
                const Icon = getIcon(b.icon);
                return (
                  <div key={i} className="bg-[#111] border border-[#262626] rounded p-6 hover:border-[#F5A623] transition-colors">
                    <div className="w-12 h-12 rounded bg-[#F5A623]/10 border border-[#F5A623]/20 flex items-center justify-center mb-4">
                      <Icon size={22} className="text-[#F5A623]" />
                    </div>
                    <h3 className="font-['Bebas_Neue'] text-xl text-white tracking-wide">{b.title}</h3>
                    {b.description && <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{b.description}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 5. OFFICIAL NETWORK */}
      {(p.website_url || (p.socials || []).length > 0) && (
        <section className="py-16 px-6 border-t border-[#262626]" data-testid="links-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">
              ΕΠΙΣΗΜΟ <span className="text-[#F5A623]">ΔΙΚΤΥΟ</span>
            </h2>
            <div className="w-12 h-1 bg-[#F5A623] mb-8" />

            <div className="flex flex-wrap gap-3">
              {p.website_url && (
                <a href={p.website_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#F5A623] text-black font-semibold rounded hover:bg-[#e6951a] transition-colors text-sm"
                  data-testid="partner-website-btn">
                  <Globe size={14} /> ΕΠΙΣΗΜΟ WEBSITE <ExternalLink size={12} />
                </a>
              )}
              {p.socials.map((s, i) => {
                const Icon = SOCIAL_ICONS[s.platform] || Globe;
                return (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-semibold rounded hover:bg-white/5 transition-colors text-sm"
                    data-testid={`partner-social-${s.platform}`}>
                    <Icon size={14} className="text-[#F5A623]" /> {s.platform.toUpperCase()}
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 6. CTA */}
      {(p.cta_title || p.cta_button_1_label) && (
        <section className="py-16 px-6 border-t border-[#262626]" data-testid="cta-section">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-[#F5A623]/10 via-[#111] to-black border border-[#F5A623]/20 rounded p-10 md:p-16">
              {p.cta_title && (
                <h2 className="font-['Bebas_Neue'] text-3xl md:text-5xl text-white tracking-wide">{p.cta_title}</h2>
              )}
              {p.cta_text && (
                <p className="text-zinc-300 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">{p.cta_text}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                {p.cta_button_1_label && p.cta_button_1_url && (
                  <Link to={rewriteCta(p.cta_button_1_url, p.slug)}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-[#F5A623] text-black font-semibold rounded hover:bg-[#e6951a] transition-colors text-sm"
                    data-testid="cta-btn-1">
                    {p.cta_button_1_label}
                  </Link>
                )}
                {p.cta_button_2_label && p.cta_button_2_url && (
                  <Link to={rewriteCta(p.cta_button_2_url, p.slug)}
                    className="inline-flex items-center gap-2 px-8 py-3 border border-white/20 text-white font-semibold rounded hover:bg-white/5 transition-colors text-sm"
                    data-testid="cta-btn-2">
                    {p.cta_button_2_label}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PartnerDetailPage;
